import { Injectable } from '@angular/core';
import { SearchResultsModel } from '../models/search-results-model';
import { ResultSelectionService } from './result-selection.service';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, of, EMPTY } from 'rxjs';
import { expand, reduce, tap, map, last } from 'rxjs/operators';
import { QueryStateService } from './query-state.service';
import { PubSearchService } from './pub-search.service';
import { SearchOutputModel } from '../models/search-output-model';
import { MessageService } from 'primeng/api';
import { ConstantsService } from './constants.service';
import { FoldersService } from './folders.service';
import { SearchFilter } from '../models/search-filter-model';

@Injectable({
  providedIn: 'root'
})
export class ZipDownloadService {

  constructor(private selectionSvc: ResultSelectionService,
    private queryStateSvc: QueryStateService,
    private searchSvc: PubSearchService,
    private messageSvc: MessageService,
    private folderSvc: FoldersService,
    private constants: ConstantsService) {

  }

  private isDownloadingSubject = new BehaviorSubject<boolean>(false);
  public isDownloading$ = this.isDownloadingSubject.asObservable();

  private downloadedItems: SearchResultsModel[] = [];
  private batchSize_max = 100; // this is used for the query to get the items for select all
  private pageCount = 0;
  private indexTotal? = 0;

  setIsDownloading(data: boolean) {
    this.isDownloadingSubject.next(data);
  }

  getFiles(): Observable<SearchResultsModel[]> {
    if (this.selectionSvc.isAllSelected() || this.selectionSvc.isAllExcept()) {
      return this.ensureAllData();
    }

    return of(this.selectionSvc.getSelected());
  }

  ensureAllData(): Observable<SearchResultsModel[]> {
    const max = environment.download_files_max;

    this.downloadedItems = [];
    this.pageCount = 0;
    this.indexTotal = 0;

    const sr = this.queryStateSvc.getUpdatedSearchRequest();   
    
    //Update Search Request to get the data for the selected folder.
    let selectedFolder = this.folderSvc.getSelectedFolder();

    if (selectedFolder.trim() !== '') {
      selectedFolder = this.folderSvc.ensureRootFolderSlash(selectedFolder);
      let fldrFilter: SearchFilter = {
        field: 'FoldersFiledIn',
        operator: this.constants.text_operator_equals,
        value: selectedFolder
      };
      sr.filters?.push(fldrFilter);
    } 

    return of(true).pipe(
      expand((_) => {
        const remaining = max - this.downloadedItems.length;
        const numReturned = this.downloadedItems.length;
        const batchSize = this.batchSize_max;
        
        // console.log('expand iteration, remaining:', remaining);
        // console.log('countReturned: ', numReturned);
        // console.log('batch Size: ', batchSize);

        // stop recursion
        // number returned < batchSize means that there were items stripped out in the process on the server side.
        if (numReturned >= max) {
          // console.log('No more remaining items. Ending expansion.');
          return EMPTY as Observable<boolean>;
        }

        // const batchSize = Math.min(this.batchSize_max, remaining);
        sr.size = this.batchSize_max;
        sr.skip = batchSize * this.pageCount;

        // console.log('Calling API with size:', batchSize, 'skip:', sr.skip);

        return this.searchSvc.downloadItems(sr).pipe(
          tap((data: SearchOutputModel) => {
            const results = data?.results || [];
            
            // console.log('API returned', results.length, 'results');
                      
            this.indexTotal = data.originalCount;
            // console.log('indexTotal: ', this.indexTotal);
            // console.log('original Count: ', data.originalCount);

            this.downloadedItems.push(...results);
            this.filterExceptions();
            // console.log('filtered item count:' + this.downloadedItems.length);
            this.pageCount++;

            // console.log('downloadedItems length:', this.downloadedItems.length);
          }),
          // must return boolean to match the outer observable type
          map(() => true)
        );
      }),
      // when expand completes, emit final aggregated value
      last(),
      map(() => this.downloadedItems)
    );
  }


  // getFiles(): SearchResultsModel[] {
  //   // Reset items to download
  //   this.downloadedItems = [];
  //   this.pageCount = 0;

  //   // Get selected files and ensure we can get all the items based on the selection.
  //   //All Except should be true if isAllSelected is true.
  //   if (this.selectionSvc.isAllSelected() || this.selectionSvc.isAllExcept()) {
  //     return this.ensureAllData();
  //     // console.log('Returning after ensure data.');
  //     // return this.downloadedItems; 
  //   }
  //   else {
  //     this.downloadedItems = this.selectionSvc.getSelected();
  //     return this.downloadedItems;
  //     // this.downloadSelectedDocuments();
  //   }
  // }



  // Fetch all the items needed to execute the file downloads
  // ensureAllData() : SearchResultsModel[] {
  //   const max = environment.download_files_max;
  //   let batchSize = this.calcRemainingBatchSize();
  //   let downloadItemCount = this.downloadedItems.length;

  //   let tc = this.selectionSvc.getCount();

  //   // Limit the download to the max items
  //   if (tc > max) {
  //     tc = max;
  //   }

  //   let sr = this.queryStateSvc.getUpdatedSearchRequest();
  //   sr.size = batchSize;
  //   sr.skip = this.batchSize_max * this.pageCount;
  //   // console.log('dwnld search req: ' + JSON.stringify(sr));

  //   // if (!this.getCancelDownload()) {
  //   if (downloadItemCount < tc || sr.skip < downloadItemCount) {
  //     //get the next batch of data
  //     console.log('sending query to get items?....')
  //     this.searchSvc.downloadItems(sr).subscribe((data: SearchOutputModel) => {
  //       if (data.results) {
  //         this.downloadedItems = this.downloadedItems.concat(data.results);
  //         this.filterExceptions();
  //         this.pageCount++;
  //       }

  //       this.ensureAllData();
  //     });
  //     // console.log('return empty');
  //     // return [];
  //   }
  //   else {
  //     return this.downloadedItems;
  //     // this.downloadSelectedDocuments();
  //   }
  // }

  calcRemainingBatchSize(): number {
    const downloadItemCount = this.downloadedItems.length;
    const tc = this.selectionSvc.getCount();
    const max = environment.download_files_max;
    const remainder = max - downloadItemCount;
    let batchSize = this.batchSize_max;

    if ((downloadItemCount < tc) && (remainder < this.batchSize_max)) {
      batchSize = remainder;
    }

    return batchSize;
  }

  filterExceptions() {
    let ex = this.selectionSvc.getExcluded();
    this.downloadedItems = this.downloadedItems.filter(item => !ex.some(e => e.document.AccessionNumber == item.document.AccessionNumber));
  }

  downloadSelectedDocuments() {
    // remove the legacy items
    this.downloadedItems = this.downloadedItems.filter(i => i.document.ItemType != 'legacy');

    let items = this.createDownloadItemsList();
    this.selectionSvc.setDownloadableCount(items.length);

    const worker = new Worker(new URL('assets/workers/download-worker.js', environment.download_worker_host));

    worker.postMessage({ items });

    worker.onmessage = (event) => {
      const message = event.data;
      switch (message.type) {
        case 'save':
          this.saveFile(message.blob, message.fileName);
          break;
        case 'error':
          this.setIsDownloading(false);
          this.messageSvc.add({ key: 'downloadFiles', severity: 'error', summary: 'There was an error Downloading Files' });
          // console.error(message.message);
          break;
        case 'complete':
          this.setIsDownloading(false);
          if (items.length < 1) {
            this.messageSvc.add({ key: 'downloadFiles', severity: 'error', summary: 'No Files to Download!', detail: 'Only Legacy items were Selected!' });
          } else {
            this.messageSvc.add({ key: 'downloadFiles', severity: 'success', summary: 'Download Complete!', detail: 'Files are in your downloads folder!' });
          }
          break;
      }
    };

    worker.onerror = (error) => {
      console.error('Web Worker error: ', error.message);
    };
  }

  private saveFile(blob: Blob, fileName: string): void {
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private createDownloadItemsList(): any[] {
    const items = this.downloadedItems.map(item => ({
      accessionNumber: item.document.AccessionNumber,
      url: item.document.Url
    }));

    return items;
  }

}


